import React, { useEffect, useState } from "react";
import { Row, Col, Card, Statistic, Table, message, Select, DatePicker } from "antd";
import {
    BarChartOutlined,
    ExperimentOutlined,
    UserOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    StopOutlined
} from "@ant-design/icons";
import { oneKlicks } from "../../SDK/oneclick";
import { getuserdata } from "../../services/apiService";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
} from 'chart.js';
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2';
import moment from 'moment';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

const { RangePicker } = DatePicker;

const DiagnosticReports = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalTests: 0,
        totalPatients: 0,
        totalAppointments: 0,
        completedAppointments: 0
    });

    // Chart Data States
    const [testCategoryData, setTestCategoryData] = useState(null);
    const [appointmentStatusData, setAppointmentStatusData] = useState(null);
    const [patientGenderData, setPatientGenderData] = useState(null);
    const [recentAppointments, setRecentAppointments] = useState([]);
    const [dateRange, setDateRange] = useState('all'); // all, week, month, year

    useEffect(() => {
        loadData();
    }, [dateRange]);

    const loadData = async () => {
        try {
            setLoading(true);
            const userId = await getuserdata('id');
            const conditions = { diagnostic_id: userId };

            // Fetch Data
            const [testsRes, patientsRes, appointmentsRes] = await Promise.all([
                oneKlicks.read('', 'diagnostic_tests', conditions),
                oneKlicks.read('', 'diagnostic_patients', conditions),
                oneKlicks.read('', 'diagnostic_appointments', conditions)
            ]);

            const tests = testsRes?.data || [];
            const patients = patientsRes?.data || [];
            const appointments = appointmentsRes?.data || [];

            // --- Stats Calculation ---
            setStats({
                totalTests: tests.length,
                totalPatients: patients.length,
                totalAppointments: appointments.length,
                completedAppointments: appointments.filter(a => a.status === 'completed').length
            });

            // --- Charts Data Preparation ---

            // 1. Tests by Category (Bar Chart)
            const categories = {};
            tests.forEach(test => {
                const cat = test.category || 'Uncategorized';
                categories[cat] = (categories[cat] || 0) + 1;
            });

            setTestCategoryData({
                labels: Object.keys(categories),
                datasets: [{
                    label: 'Number of Tests',
                    data: Object.values(categories),
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                }]
            });

            // 2. Appointment Status (Doughnut Chart)
            const statuses = {
                pending: 0,
                confirmed: 0,
                completed: 0,
                cancelled: 0
            };
            appointments.forEach(apt => {
                const status = apt.status?.toLowerCase() || 'pending';
                if (statuses[status] !== undefined) statuses[status]++;
            });

            setAppointmentStatusData({
                labels: ['Pending', 'Confirmed', 'Completed', 'Cancelled'],
                datasets: [{
                    data: [statuses.pending, statuses.confirmed, statuses.completed, statuses.cancelled],
                    backgroundColor: [
                        '#faad14', // Pending - Warning/Orange
                        '#1890ff', // Confirmed - Blue
                        '#52c41a', // Completed - Green
                        '#ff4d4f'  // Cancelled - Red
                    ],
                    borderWidth: 1,
                }]
            });

            // 3. Patient Gender Distribution (Pie Chart)
            const genders = { Male: 0, Female: 0, Other: 0 };
            patients.forEach(p => {
                const g = p.gender || 'Other';
                if (genders[g] !== undefined) genders[g]++;
                else genders.Other++;
            });

            setPatientGenderData({
                labels: Object.keys(genders),
                datasets: [{
                    data: Object.values(genders),
                    backgroundColor: ['#36a2eb', '#ff6384', '#ffce56'],
                    borderWidth: 1,
                }]
            });

            // Recent Activity
            setRecentAppointments(appointments.slice(0, 5).map(item => ({
                key: item.id,
                patient: item.patient_name,
                test: item.test_name,
                date: item.appointment_date,
                status: item.status
            })));

        } catch (error) {
            console.error('Error loading reports:', error);
            message.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const StatusBadge = ({ status }) => {
        let color = '#d9d9d9';
        let icon = null;
        switch (status) {
            case 'completed': color = '#52c41a'; icon = <CheckCircleOutlined />; break;
            case 'confirmed': color = '#1890ff'; icon = <CheckCircleOutlined />; break;
            case 'pending': color = '#faad14'; icon = <ClockCircleOutlined />; break;
            case 'cancelled': color = '#ff4d4f'; icon = <StopOutlined />; break;
            default: break;
        }
        return <span style={{ color }}>{icon} {status?.toUpperCase()}</span>;
    };

    const columns = [
        { title: 'Patient', dataIndex: 'patient', key: 'patient' },
        { title: 'Test', dataIndex: 'test', key: 'test' },
        { title: 'Date', dataIndex: 'date', key: 'date', render: (date) => date ? new Date(date).toLocaleDateString() : '-' },
        { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => <StatusBadge status={status} /> }
    ];

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="font-weight-bold mb-0">
                        <BarChartOutlined className="me-2 text-primary" />
                        Diagnostic Reports
                    </h4>
                    <p className="text-muted mb-0">Analytics and overview.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Select defaultValue="all" style={{ width: 120 }} onChange={setDateRange}>
                        <Select.Option value="all">All Time</Select.Option>
                        <Select.Option value="week">This Week</Select.Option>
                        <Select.Option value="month">This Month</Select.Option>
                        <Select.Option value="year">This Year</Select.Option>
                    </Select>
                </div>
            </div>

            {/* Stats Overview */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} className="shadow-sm">
                        <Statistic
                            title="Total Tests"
                            value={stats.totalTests}
                            prefix={<ExperimentOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} className="shadow-sm">
                        <Statistic
                            title="Total Patients"
                            value={stats.totalPatients}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} className="shadow-sm">
                        <Statistic
                            title="Total Appointments"
                            value={stats.totalAppointments}
                            prefix={<CalendarOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} className="shadow-sm">
                        <Statistic
                            title="Completed"
                            value={stats.completedAppointments}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                            suffix={`/ ${stats.totalAppointments}`}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Charts Row 1 */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} lg={12}>
                    <Card title="Appointment Status" bordered={false} className="shadow-sm h-100">
                        <div style={{ height: '300px', display: 'flex', justifyContent: 'center' }}>
                            {appointmentStatusData && <Doughnut data={appointmentStatusData} options={{ maintainAspectRatio: false }} />}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Tests by Category" bordered={false} className="shadow-sm h-100">
                        <div style={{ height: '300px' }}>
                            {testCategoryData && <Bar data={testCategoryData} options={{ maintainAspectRatio: false, scales: { y: { beginAtZero: true } } }} />}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Charts Row 2 & Table */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={8}>
                    <Card title="Patient Gender" bordered={false} className="shadow-sm h-100">
                        <div style={{ height: '250px', display: 'flex', justifyContent: 'center' }}>
                            {patientGenderData && <Pie data={patientGenderData} options={{ maintainAspectRatio: false }} />}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={16}>
                    <Card title="Recent Appointments" bordered={false} className="shadow-sm h-100">
                        <Table
                            columns={columns}
                            dataSource={recentAppointments}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DiagnosticReports;
